import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Content_Kind, Conversation_Kind } from '@gen/chat/v1/common_pb';
import type { DeleteMessageRequest_Scope } from '@gen/chat/v1/methods_p1_pb';
import { useStrings } from '@/i18n/Localization';
import { Avatar } from '@/components/Avatar';
import { useConversationPhoto, usePresence } from '@/screens/useConversationHeader';
import { Composer, type PendingMention } from '@/components/Composer';
import { DeleteDialog } from '@/components/DeleteDialog';
import { ForwardDialog } from '@/components/ForwardDialog';
import { PinnedPanel } from '@/components/PinnedPanel';
import { CreatePollDialog } from '@/components/Poll';
import { SearchPanel } from '@/components/SearchPanel';
import { Timeline } from '@/components/Timeline';
import type { RecordingKind } from '@/components/VoiceRecorder';
import { playback } from '@/media/playbackManager';
import { AttachmentTooLargeError, attachmentFromUpload, createUploadRecord } from '@/media/uploads';
import { verifyEntities } from '@/protocol/entities';
import {
  ALBUM_MAX,
  DRAFT_SYNC_IDLE_MS,
  HISTORY_PAGE_SIZE,
  MEMBER_PAGE_SIZE,
  TOPIC_PAGE_SIZE,
} from '@/protocol/limits';
import {
  canDeleteOthers as canDeleteOthersFor,
  canPin as canPinFor,
  canPublish,
  canSend,
  exemptFromSlowMode,
  viewerFor,
} from '@/protocol/permissions';
import { useLiveQuery } from '@/store/hooks';
import { db } from '@/store/db';
import {
  deleteDraft,
  discardOperation,
  enqueueClosePoll,
  enqueueCreatePoll,
  enqueueDeleteMessage,
  enqueueEditMessage,
  enqueueForwardMessage,
  enqueueMarkRead,
  enqueuePublishPost,
  enqueueSendMessage,
  enqueueSetDraft,
  enqueueSetPin,
  enqueueSetPlaybackPosition,
  enqueueSetReaction,
  enqueueVote,
  getConversation,
  getDraft,
  getMember,
  listMembers,
  listOutboxForConversation,
  listReactions,
  listTopics,
  listUploadsForConversation,
  loadLatestMessages,
  resolveConversationAlias,
  resolveDraftConflict,
  retryOperation,
  saveDraft,
  shouldRecordView,
} from '@/store/localStore';
import { useSession } from '@/transport/SessionProvider';
import type {
  ConversationRecord,
  DraftRecord,
  MemberRecord,
  MessageRecord,
  OutboxRecord,
  PollRecord,
  ReactionRecord,
  TopicRecord,
  UploadRecord,
} from '@/store/types';
import type { Hex } from '@/store/ids';
import panels from '@/components/panels.module.css';
import styles from '@/screens/ConversationScreen.module.css';

/** How long a keystroke waits before the draft is written locally. */
const DRAFT_DEBOUNCE_MS = 400;

/**
 * The conversation screen.
 *
 * Everything rendered here comes from LocalStore through `useLiveQuery`. There
 * is no second message array fed from the socket, which is the Section 14 rule
 * and also the reason an offline reload shows exactly what was on screen
 * before: the durable store is the only source.
 *
 * The local page size grows as older pages are loaded, so the store query and
 * the server query stay aligned on the same keyset boundary.
 *
 * Phase 2 adds two independent draft timers, and the difference between them
 * matters. The local write is debounced by 400 ms so that typing does not put
 * an IndexedDB transaction in the keystroke path. The *server* synchronization
 * is debounced by one second, which is the interval Section 06 fixes, and it
 * carries a version so that a second device editing the same draft produces a
 * conflict the user resolves rather than a silent overwrite.
 */
export function ConversationScreen() {
  const { t, formatRelativeDay } = useStrings();
  const { account, worker } = useSession();
  const navigate = useNavigate();
  const params = useParams<{ conversationId: string }>();
  const conversationId = (params.conversationId ?? '') as Hex;

  const [pageSize, setPageSize] = useState(HISTORY_PAGE_SIZE);
  const [draftText, setDraftText] = useState('');
  const [replyTo, setReplyTo] = useState<{ id: Hex; name: string } | null>(null);
  const [editing, setEditing] = useState<MessageRecord | null>(null);
  const [deleting, setDeleting] = useState<MessageRecord | null>(null);
  const [forwarding, setForwarding] = useState<MessageRecord | null>(null);
  const [localFailure, setLocalFailure] = useState<string | null>(null);
  const [ownSendToken, setOwnSendToken] = useState(0);
  const [history, setHistory] = useState({ hasMore: true, loading: false });
  const [topicId, setTopicId] = useState<Hex | null>(null);
  const [silent, setSilent] = useState(false);
  const [creatingPoll, setCreatingPoll] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [pinsOpen, setPinsOpen] = useState(false);
  const [focusMessageId, setFocusMessageId] = useState<string | null>(null);
  const [autoPlayMessageId, setAutoPlayMessageId] = useState<string | null>(null);
  const [slowModeRemaining, setSlowModeRemaining] = useState(0);

  const draftLoadedFor = useRef<string>('');
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * The draft content most recently sent to the server.
   *
   * Without it the synchronization below would run forever: a successful
   * `SetDraft` writes the new version onto the draft row, the live query fires,
   * the effect re-runs, and it sends the same text again under the new version.
   * Comparing content rather than the whole record is what breaks that cycle,
   * because the version is exactly the field that changes as a *result* of
   * syncing rather than a reason to sync.
   */
  const lastSyncedDraft = useRef<string | null>(null);
  /** Highest sequence this screen has already queued a read receipt for. */
  const lastMarkedRef = useRef(0);

  // The wrapper distinguishes "not read yet" from "read, and absent". Without
  // it the first render of every conversation would flash the "not available on
  // this device" notice before the query settles.
  // The conversation, and where it went if this identifier is a stale one.
  //
  // A conversation created here is opened under a provisional identifier while
  // the server is still being asked for the real one, so the address bar holds
  // an identifier that is about to stop existing. Reading the forwarding
  // address in the same query means the rename is observed rather than raced:
  // without it the conversation appeared, then vanished the instant the server
  // answered, which is a worse failure than never opening at all.
  const conversationQuery = useLiveQuery<{
    loaded: boolean;
    record: ConversationRecord | null;
    renamedTo: Hex | null;
  }>(
    async () => {
      const record = await getConversation(conversationId);
      return {
        loaded: true,
        record,
        renamedTo: record ? null : await resolveConversationAlias(conversationId),
      };
    },
    [conversationId],
    { loaded: false, record: null, renamedTo: null },
  );
  const conversation = conversationQuery.record;
  const renamedTo = conversationQuery.renamedTo;

  useEffect(() => {
    if (renamedTo === null) {
      return;
    }
    // Replace rather than push: the provisional identifier is not somewhere the
    // user should be able to navigate back to, because it will never resolve
    // again.
    void navigate(`/c/${renamedTo}`, { replace: true });
  }, [renamedTo, navigate]);

  const messages = useLiveQuery<MessageRecord[]>(
    () => loadLatestMessages(conversationId, pageSize),
    [conversationId, pageSize],
    [],
  );

  const outbox = useLiveQuery<OutboxRecord[]>(
    () => listOutboxForConversation(conversationId),
    [conversationId],
    [],
  );

  const uploads = useLiveQuery<UploadRecord[]>(
    () => listUploadsForConversation(conversationId),
    [conversationId],
    [],
  );

  const reactions = useLiveQuery<ReactionRecord[]>(
    () => listReactions(conversationId),
    [conversationId],
    [],
  );

  const members = useLiveQuery<MemberRecord[]>(
    () => listMembers(conversationId),
    [conversationId],
    [],
  );

  const topics = useLiveQuery<TopicRecord[]>(
    () => listTopics(conversationId),
    [conversationId],
    [],
  );

  const ownMember = useLiveQuery<MemberRecord | null>(
    () => (account ? getMember(conversationId, account.id) : Promise.resolve(null)),
    [conversationId, account?.id],
    null,
  );

  const draft = useLiveQuery<DraftRecord | null>(
    () => getDraft(conversationId),
    [conversationId],
    null,
  );

  // Polls referenced by the loaded window. Reading them by identifier rather
  // than by conversation keeps the query proportional to what is on screen.
  const pollIds = useMemo(
    () =>
      messages
        .map((message) => message.content.embeddedId)
        .filter((id): id is Hex => id !== null),
    [messages],
  );
  const polls = useLiveQuery<PollRecord[]>(
    async () => {
      if (pollIds.length === 0) {
        return [];
      }
      const rows = await db.polls.bulkGet([...pollIds]);
      return rows.filter((row): row is PollRecord => row !== undefined);
    },
    [pollIds.join(',')],
    [],
  );
  const pollsById = useMemo(
    () => new Map(polls.map((poll) => [poll.pollId, poll])),
    [polls],
  );

  const outboxByOp = useMemo(() => {
    const map = new Map<string, OutboxRecord>();
    for (const record of outbox) {
      map.set(record.opId, record);
    }
    return map;
  }, [outbox]);

  const viewer = viewerFor(conversation, ownMember);
  const isGroup = conversation?.kind === Conversation_Kind.GROUP;
  const isChannel = conversation?.kind === Conversation_Kind.CHANNEL;
  const canPin = canPinFor(viewer);
  const canDeleteOthers = canDeleteOthersFor(viewer);

  // Tell the worker which conversation matters, so sync prioritizes it and
  // subscribes to its live events.
  useEffect(() => {
    worker?.setActiveConversation(conversationId === '' ? null : conversationId);
    return () => {
      worker?.setActiveConversation(null);
    };
  }, [worker, conversationId]);

  useEffect(() => {
    if (!worker) {
      return;
    }
    return worker.onHistory((id, state) => {
      if (id !== conversationId) {
        return;
      }
      setHistory({ hasMore: state.hasMore, loading: state.loading });
    });
  }, [worker, conversationId]);

  // Group membership and topics are needed for mentions, moderation controls
  // and the topic selector. They are refreshed on entry and otherwise read from
  // the store, so an offline conversation still resolves the names it knows.
  useEffect(() => {
    if (!worker || !(isGroup || isChannel)) {
      return;
    }
    void worker
      .call('list-members', { conversationId, cursor: null, limit: MEMBER_PAGE_SIZE })
      .catch(() => undefined);
    if (isGroup) {
      void worker
        .call('list-topics', { conversationId, cursor: null, limit: TOPIC_PAGE_SIZE })
        .catch(() => undefined);
    }
  }, [worker, conversationId, isGroup, isChannel]);

  // Load the stored draft when the conversation changes. Section 14 requires
  // drafts to survive navigation, and the guard stops a freshly typed character
  // from being overwritten by the asynchronous read that started before it.
  useEffect(() => {
    if (draftLoadedFor.current === conversationId) {
      return;
    }
    draftLoadedFor.current = conversationId;
    lastMarkedRef.current = 0;
    // The signature belongs to the conversation that was open, so a different
    // conversation whose draft happens to read the same must still be synced.
    lastSyncedDraft.current = null;
    setDraftText('');
    setReplyTo(null);
    setEditing(null);
    setTopicId(null);
    setPageSize(HISTORY_PAGE_SIZE);
    setFocusMessageId(null);
    setAutoPlayMessageId(null);

    void getDraft(conversationId).then((stored) => {
      if (draftLoadedFor.current !== conversationId || !stored) {
        return;
      }
      setDraftText(stored.text);
      setTopicId(stored.topicId);
      if (stored.replyTo !== null) {
        setReplyTo({
          id: stored.replyTo as Hex,
          name: stored.replyToName ?? t('conversation.unknownSender'),
        });
      }
    });
  }, [conversationId, t]);

  // Persist the draft after a pause rather than on every keystroke: a write per
  // character would put an IndexedDB transaction in the typing path.
  useEffect(() => {
    if (draftLoadedFor.current !== conversationId || editing) {
      return;
    }
    if (draftTimer.current !== null) {
      clearTimeout(draftTimer.current);
    }
    const text = draftText;
    const reply = replyTo;
    const topic = topicId;
    const pendingUploadIds = uploads
      .filter((upload) => upload.state !== 'READY' && upload.state !== 'CANCELLED')
      .map((upload) => upload.uploadId);
    const version = draft?.version ?? 0;
    const conflict = draft?.conflict ?? null;

    draftTimer.current = setTimeout(() => {
      void (text.trim().length === 0 && reply === null && pendingUploadIds.length === 0
        ? deleteDraft(conversationId)
        : saveDraft({
            conversationId,
            text,
            replyTo: reply?.id ?? null,
            replyToName: reply?.name ?? null,
            updatedAt: Date.now(),
            version,
            pendingUploadIds,
            topicId: topic,
            conflict,
          }));
    }, DRAFT_DEBOUNCE_MS);

    return () => {
      if (draftTimer.current !== null) {
        clearTimeout(draftTimer.current);
      }
    };
  }, [draftText, replyTo, topicId, conversationId, editing, uploads, draft?.version, draft?.conflict]);

  /**
   * Section 06: "sync after 1 second idle with version checking".
   *
   * A separate timer from the local write above, because they answer different
   * questions — one is "do not lose what I typed", the other is "let my other
   * devices see it" — and collapsing them would either put a network mutation
   * in the keystroke path or delay the local save by a second.
   */
  useEffect(() => {
    if (draftLoadedFor.current !== conversationId || editing || !account) {
      return;
    }
    if (draftSyncTimer.current !== null) {
      clearTimeout(draftSyncTimer.current);
    }
    const stored = draft;
    if (!stored || stored.conflict !== null) {
      // Nothing to send, or an unresolved conflict. Sending during a conflict
      // would resolve it by force, which is exactly what Section 06 forbids.
      return;
    }
    const signature = JSON.stringify([
      stored.text,
      stored.replyTo,
      stored.topicId,
      stored.pendingUploadIds,
    ]);
    if (lastSyncedDraft.current === signature) {
      return;
    }
    draftSyncTimer.current = setTimeout(() => {
      lastSyncedDraft.current = signature;
      void enqueueSetDraft({
        conversationId,
        originDeviceId: account.deviceId,
        draft: stored,
      })
        .then(() => {
          worker?.notifyOutboxChanged();
        })
        .catch(() => {
          // A draft that could not be queued is not worth interrupting the user
          // for: the local copy is safe, and clearing the marker means the next
          // keystroke retries rather than assuming this one landed.
          lastSyncedDraft.current = null;
        });
    }, DRAFT_SYNC_IDLE_MS);

    return () => {
      if (draftSyncTimer.current !== null) {
        clearTimeout(draftSyncTimer.current);
      }
    };
  }, [draft, conversationId, editing, account, worker]);

  // Mark the conversation read once its newest message is on screen.
  //
  // The in-memory guard matters as well as the store's own check: the store
  // update that records the new read position lands after the enqueue resolves,
  // so two renders in quick succession could otherwise both pass the durable
  // check and queue the same position twice.
  useEffect(() => {
    const newest = messages[messages.length - 1];
    if (!account || !conversation || !newest || newest.createdSeq === 0) {
      return;
    }
    if (conversation.lastReadSeq >= newest.createdSeq) {
      return;
    }
    if (lastMarkedRef.current >= newest.createdSeq) {
      return;
    }
    lastMarkedRef.current = newest.createdSeq;
    void enqueueMarkRead({
      conversationId,
      originDeviceId: account.deviceId,
      lastReadCreatedSeq: newest.createdSeq,
    }).then((enqueued) => {
      if (enqueued) {
        worker?.notifyOutboxChanged();
      }
    });
  }, [messages, conversation, account, conversationId, worker]);

  /**
   * Section 14: auto-play the next voice note in the open chat while playback
   * stays active, and stop on navigation.
   *
   * The queue is the voice messages of the loaded window in timeline order. It
   * is registered under the conversation identifier so that leaving clears only
   * this conversation's queue, and the manager pauses everything on unmount.
   */
  useEffect(() => {
    const voiceKeys = messages
      .filter(
        (message) =>
          message.content.attachments.length > 0 && message.content.kind === Content_Kind.VOICE,
      )
      .map((message) => message.id);
    playback.setQueue(conversationId, voiceKeys, (key) => {
      setAutoPlayMessageId(key);
    });
    return () => {
      playback.clearQueue(conversationId);
      playback.stopAll();
    };
  }, [conversationId, messages]);

  /**
   * Section 06 slow mode, as a countdown the user can see.
   *
   * The server enforces the limit; this is only the explanation. An
   * administrator is exempt, and a conversation with slow mode off never runs
   * the timer at all.
   */
  useEffect(() => {
    const seconds = conversation?.slowModeSeconds ?? 0;
    if (seconds === 0 || exemptFromSlowMode(viewer)) {
      setSlowModeRemaining(0);
      return;
    }
    const tick = () => {
      const elapsed = Date.now() - (conversation?.lastOwnSendAt ?? 0);
      setSlowModeRemaining(Math.max(0, Math.ceil((seconds * 1000 - elapsed) / 1000)));
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [conversation?.slowModeSeconds, conversation?.lastOwnSendAt, viewer]);

  const loadOlder = useCallback(() => {
    // Two things happen, and both are needed. The local window grows so the
    // store query returns pages already on this device, and the worker fetches
    // the next server page. On a device that already holds the history the
    // first alone is enough and the second returns nothing new; offline, the
    // second fails and the first still works.
    setPageSize((size) => size + HISTORY_PAGE_SIZE);
    worker?.loadHistory(conversationId, true);
  }, [worker, conversationId]);

  const queued = useCallback(() => {
    worker?.notifyOutboxChanged();
  }, [worker]);

  // ---------------------------------------------------------------------
  // Attachments
  // ---------------------------------------------------------------------

  const addFiles = useCallback(
    async (files: File[]) => {
      setLocalFailure(null);
      // Section 06 caps an album at ten objects, so the excess is refused where
      // the user can see it rather than at send time.
      const room = ALBUM_MAX - uploads.filter((upload) => upload.state !== 'CANCELLED').length;
      if (room <= 0) {
        setLocalFailure(t('attach.tooMany', { count: ALBUM_MAX }));
        return;
      }
      for (const file of files.slice(0, room)) {
        try {
          const record = await createUploadRecord({
            conversationId,
            file,
            filename: file.name,
          });
          // The transfer starts only after the record exists, so a persistence
          // failure never produces an upload with nothing tracking it.
          await worker?.call('upload-start', { uploadId: record.uploadId });
        } catch (error) {
          setLocalFailure(
            error instanceof AttachmentTooLargeError
              ? t('attach.tooLarge')
              : t('status.localFailure'),
          );
        }
      }
    },
    [conversationId, t, uploads, worker],
  );

  const addRecording = useCallback(
    async (blob: Blob, durationMs: number, mimeType: string, kind: RecordingKind) => {
      setLocalFailure(null);
      try {
        const extension = mimeType.includes('mp4') ? 'm4a' : 'webm';
        const record = await createUploadRecord({
          conversationId,
          file: blob,
          filename: `${kind === 'voice' ? 'voice' : 'video'}-${String(Date.now())}.${extension}`,
          mediaKind: kind,
          durationMs,
        });
        await worker?.call('upload-start', { uploadId: record.uploadId });
      } catch {
        setLocalFailure(t('status.localFailure'));
      }
    },
    [conversationId, t, worker],
  );

  const discardRecording = useCallback(() => {
    // The most recent transfer is the one the recorder just created; discarding
    // during review cancels it rather than leaving an orphan in the list.
    const latest = uploads[uploads.length - 1];
    if (latest) {
      void worker?.call('upload-cancel', { uploadId: latest.uploadId });
    }
  }, [uploads, worker]);

  // ---------------------------------------------------------------------
  // Sending
  // ---------------------------------------------------------------------

  const send = async (text: string, mentions: PendingMention[]) => {
    if (!account) {
      return;
    }
    setLocalFailure(null);

    const ready = uploads
      .map(attachmentFromUpload)
      .filter((attachment): attachment is NonNullable<typeof attachment> => attachment !== null);
    const kinds = uploads
      .filter((upload) => upload.state === 'READY')
      .map((upload) => upload.mediaKind);

    try {
      if (editing) {
        await enqueueEditMessage({
          conversationId,
          messageId: editing.id as Hex,
          originDeviceId: account.deviceId,
          expectedVersion: editing.version,
          text,
        });
        setEditing(null);
      } else if (isChannel && canPublish(viewer)) {
        await enqueuePublishPost({
          conversationId,
          senderId: account.id,
          originDeviceId: account.deviceId,
          text,
          silent,
          attachments: ready,
          attachmentKinds: kinds,
        });
      } else {
        await enqueueSendMessage({
          conversationId,
          senderId: account.id,
          originDeviceId: account.deviceId,
          text,
          replyTo: replyTo?.id ?? null,
          topicId,
          attachments: ready,
          attachmentKinds: kinds,
          // Mentions are re-verified against the final text: an entity whose
          // range no longer reads as the name it marked is dropped, and the
          // mention degrades to ordinary text rather than pointing somewhere
          // wrong.
          entities: verifyEntities(text, mentions),
        });
        setReplyTo(null);
      }

      // The transfers have become part of a message; their local rows and the
      // bytes they hold are no longer needed on this device.
      for (const upload of uploads) {
        if (upload.state === 'READY') {
          await db.uploads.delete(upload.uploadId);
        }
      }

      setDraftText('');
      setOwnSendToken((token) => token + 1);
      // Only now, after the local transaction has committed, is the network
      // told there is work. Section 05 requires persistence to precede the
      // attempt, and this ordering is what enforces it.
      queued();
    } catch {
      // Nothing was written, so there is no bubble and no queued indicator —
      // exactly what Section 14 demands. Every failure of the enqueue path is a
      // local persistence failure by construction, because the enqueue does not
      // touch the network at all; the text stays in the composer so the user
      // does not lose it.
      setLocalFailure(t('status.localFailure'));
    }
  };

  const confirmDelete = async (scope: DeleteMessageRequest_Scope) => {
    const target = deleting;
    setDeleting(null);
    if (!target || !account) {
      return;
    }
    try {
      await enqueueDeleteMessage({
        conversationId,
        messageId: target.id as Hex,
        originDeviceId: account.deviceId,
        expectedVersion: target.version,
        scope,
      });
      queued();
    } catch {
      setLocalFailure(t('status.localFailure'));
    }
  };

  /**
   * Who is currently typing in this conversation, and until when.
   *
   * Kept as an expiry per person rather than a boolean. A stop notification is
   * ephemeral and may be dropped under pressure, so the indicator has to expire
   * on its own — otherwise somebody who typed once and closed their laptop stays
   * "typing" in this header forever.
   */
  const [typingUntil, setTypingUntil] = useState<ReadonlyMap<string, number>>(new Map());

  useEffect(() => {
    if (!worker) return;
    return worker.onTyping((typingConversation, userId, expiresAt) => {
      if (typingConversation !== conversationId) return;
      setTypingUntil((current) => {
        const next = new Map(current);
        // An expiry in the past is a stop. Treating it as "typing until then"
        // would leave an entry the sweep has to clean up a tick later.
        if (expiresAt <= Date.now()) {
          next.delete(userId);
        } else {
          next.set(userId, expiresAt);
        }
        return next;
      });
    });
  }, [worker, conversationId]);

  // One sweep while anybody is typing, stopped when nobody is. A permanent
  // interval would tick for the life of the tab to do nothing.
  useEffect(() => {
    if (typingUntil.size === 0) return;
    const timer = setInterval(() => {
      const now = Date.now();
      setTypingUntil((current) => {
        const next = new Map([...current].filter(([, expiresAt]) => expiresAt > now));
        return next.size === current.size ? current : next;
      });
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [typingUntil.size]);

  const displayNameFor = useCallback(
    (userId: string): string => {
      if (account && userId === account.id) {
        return t('conversation.you');
      }
      const member = members.find((candidate) => candidate.userId === userId);
      if (member && member.displayName.length > 0) {
        return member.displayName;
      }
      // For a direct conversation the title is the peer's name. In a group with
      // no name known yet, the identifier prefix is used rather than a made-up
      // placeholder that could be mistaken for a real name.
      return conversation?.kind === Conversation_Kind.DIRECT
        ? conversation.title
        : userId.slice(0, 8);
    },
    [account, conversation, members, t],
  );

  /**
   * The names shown in the header while people are typing.
   *
   * This account is excluded: a header that says "you are typing" tells the
   * reader something they already know and costs the line that would otherwise
   * show somebody else.
   */
  /**
   * The conversation photo, and the other party's presence in a direct chat.
   *
   * Presence is asked about only for a direct conversation: a group has no single
   * "other person" whose last-seen would mean anything, and asking about every
   * member would be a request per member for a line with no room to show them.
   */
  const photoUrl = useConversationPhoto(
    worker,
    conversationId === '' ? null : (conversationId as Hex),
    conversation?.photoMediaId ?? null,
  );

  const peerUserId = useMemo(() => {
    if (conversation?.kind !== Conversation_Kind.DIRECT || !account) return null;
    const peer = members.find((candidate) => candidate.userId !== account.id);
    return peer ? (peer.userId as Hex) : null;
  }, [conversation, members, account]);

  const presence = usePresence(worker, peerUserId);

  const typingNames = useMemo(() => {
    const now = Date.now();
    return [...typingUntil.entries()]
      .filter(([userId, expiresAt]) => expiresAt > now && userId !== account?.id)
      .map(([userId]) => displayNameFor(userId));
  }, [typingUntil, account, displayNameFor]);

  const jumpToMessage = useCallback(
    (messageId: string) => {
      setSearchOpen(false);
      setPinsOpen(false);
      setFocusMessageId(messageId);
      // The target may be older than the loaded window. Widening it is cheap —
      // the virtualizer still renders about thirty rows — and is what lets the
      // timeline scroll to a result found by a server search.
      setPageSize((size) => Math.max(size, HISTORY_PAGE_SIZE * 4));
    },
    [],
  );

  if (!account) {
    return null;
  }

  if (conversationQuery.loaded && conversation === null && renamedTo === null && messages.length === 0) {
    return (
      <main className={styles.screen} aria-label={t('nav.conversation')}>
        <p className={styles.notice}>{t('conversation.notFound')}</p>
      </main>
    );
  }

  const readOnlyChannel = isChannel && !canPublish(viewer);
  const restricted = (isGroup || isChannel) && !canSend(viewer);
  const blockedReason =
    readOnlyChannel
      ? t('channel.readOnly')
      : restricted
        ? t('group.roleRestricted')
        : slowModeRemaining > 0
          ? t('slowmode.wait', { seconds: slowModeRemaining })
          : null;

  return (
    <main className={styles.screen} aria-label={t('nav.conversation')}>
      <header className={styles.header}>
        <Link to="/" className={styles.back} aria-label={t('nav.back')} title={t('nav.back')}>
          <span aria-hidden="true">‹</span>
        </Link>
        <Avatar
          id={conversationId ?? ''}
          name={conversation?.title || t('conversation.unknownSender')}
          photoUrl={photoUrl}
          size="sm"
        />
        <div className={styles.headerText}>
          <h1 className={styles.title} dir="auto">
            {conversation?.title || t('conversation.unknownSender')}
          </h1>
          {/*
            The subtitle is whoever is typing, otherwise nothing. It is
            deliberately not a presence line: there is no presence API, and
            inventing "last seen recently" from data this client does not have
            would be a fabricated status.
          */}
          {/*
            Typing wins over presence: it is the more specific and more useful
            fact, and showing both would need two lines the header does not have.
            Presence shows only what the server disclosed — "online", or a
            last-seen time — and nothing at all when it disclosed neither, because
            hidden and offline are deliberately the same answer.
          */}
          {typingNames.length > 0 ? (
            <p className={`${styles.subtitle} ${styles.subtitleActive}`}>
              {t('conversation.typing', { names: typingNames.join(', ') })}
            </p>
          ) : presence?.online ? (
            <p className={`${styles.subtitle} ${styles.subtitleActive}`}>
              {t('presence.online')}
            </p>
          ) : presence?.lastSeenAt ? (
            <p className={styles.subtitle}>
              {t('presence.lastSeen', { when: formatRelativeDay(presence.lastSeenAt) })}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className={panels.action}
          onClick={() => {
            setSearchOpen((open) => !open);
            setPinsOpen(false);
          }}
        >
          {t('search.open')}
        </button>
        <button
          type="button"
          className={panels.action}
          onClick={() => {
            setPinsOpen((open) => !open);
            setSearchOpen(false);
          }}
        >
          {t('pin.open')}
        </button>
        {isGroup ? (
          <Link to={`/c/${conversationId}/group`} className={panels.action}>
            {t('group.openInfo')}
          </Link>
        ) : null}
        {isChannel ? (
          <Link to={`/c/${conversationId}/channel`} className={panels.action}>
            {t('channel.openInfo')}
          </Link>
        ) : null}
      </header>

      {searchOpen ? (
        <SearchPanel
          worker={worker}
          conversationId={conversationId}
          members={members}
          onOpenResult={jumpToMessage}
          onClose={() => {
            setSearchOpen(false);
          }}
        />
      ) : null}

      {pinsOpen ? (
        <PinnedPanel
          worker={worker}
          conversationId={conversationId}
          canUnpin={canPin}
          onOpen={jumpToMessage}
          onUnpin={(messageId) => {
            void enqueueSetPin({
              conversationId,
              messageId,
              originDeviceId: account.deviceId,
              pinned: false,
            }).then(queued);
          }}
          onClose={() => {
            setPinsOpen(false);
          }}
        />
      ) : null}

      <Timeline
        messages={messages}
        outboxByOp={outboxByOp}
        conversation={conversation}
        ownUserId={account.id}
        worker={worker}
        reactions={reactions}
        polls={pollsById}
        canPin={canPin}
        canDeleteOthers={canDeleteOthers}
        autoPlayMessageId={autoPlayMessageId}
        focusMessageId={focusMessageId}
        hasMore={history.hasMore}
        loadingOlder={history.loading}
        onLoadOlder={loadOlder}
        onReply={(message) => {
          setReplyTo({
            id: message.id as Hex,
            // A channel post carries its own displayed name; only an account
            // sender is looked up in the local directory.
            name:
              message.sender?.kind === 'CHANNEL'
                ? message.sender.displayName
                : message.senderId === null
                  ? ''
                  : displayNameFor(message.senderId),
          });
        }}
        onEdit={(message) => {
          setEditing(message);
          setDraftText(message.content.text);
        }}
        onDelete={(message) => {
          setDeleting(message);
        }}
        onForward={(message) => {
          setForwarding(message);
        }}
        onPin={(message) => {
          void enqueueSetPin({
            conversationId,
            messageId: message.id,
            originDeviceId: account.deviceId,
            pinned: !message.pinned,
          }).then(queued);
        }}
        onReact={(message, reaction, active) => {
          void enqueueSetReaction({
            conversationId,
            messageId: message.id,
            userId: account.id,
            originDeviceId: account.deviceId,
            reaction,
            active,
          }).then((enqueued) => {
            if (enqueued) {
              queued();
            }
          });
        }}
        onVote={(poll, optionIds) => {
          void enqueueVote({
            conversationId,
            pollId: poll.pollId,
            optionIds,
            originDeviceId: account.deviceId,
          }).then(queued);
        }}
        onClosePoll={(poll) => {
          void enqueueClosePoll({
            conversationId,
            pollId: poll.pollId,
            originDeviceId: account.deviceId,
            expectedVersion: poll.version,
          }).then(queued);
        }}
        onVoicePosition={(message, positionMs) => {
          void enqueueSetPlaybackPosition({
            conversationId,
            messageId: message.id,
            originDeviceId: account.deviceId,
            positionMs,
          }).then(queued);
        }}
        onSeen={(message) => {
          // Section 06 deduplicates a view by account, post and day; the local
          // guard keeps a scroll from spending a request per row.
          if (shouldRecordView(conversationId, message.id)) {
            void worker
              ?.call('record-view', { conversationId, messageId: message.id as Hex })
              .catch(() => undefined);
          }
        }}
        onRetry={(opId) => {
          void retryOperation(opId).then(queued);
        }}
        onDiscard={(opId) => {
          void discardOperation(opId);
        }}
        ownSendToken={ownSendToken}
        displayNameFor={displayNameFor}
      />

      <Composer
        value={draftText}
        onChange={setDraftText}
        onSend={(text, mentions) => {
          void send(text, mentions);
        }}
        replyToName={replyTo?.name ?? null}
        onCancelReply={() => {
          setReplyTo(null);
        }}
        editing={editing !== null}
        onCancelEdit={() => {
          setEditing(null);
          setDraftText('');
        }}
        disabled={conversation?.accessRevoked ?? false}
        localFailure={localFailure}
        uploads={uploads}
        onFilesChosen={(files) => {
          void addFiles(files);
        }}
        onRecorded={(blob, durationMs, mimeType, kind) => {
          void addRecording(blob, durationMs, mimeType, kind);
        }}
        onDiscardRecording={discardRecording}
        onPauseUpload={(uploadId) => {
          void worker?.call('upload-pause', { uploadId });
        }}
        onResumeUpload={(uploadId) => {
          void worker?.call('upload-start', { uploadId });
        }}
        onCancelUpload={(uploadId) => {
          void worker?.call('upload-cancel', { uploadId });
        }}
        onCreatePoll={() => {
          setCreatingPoll(true);
        }}
        blockedReason={blockedReason}
        conflictText={draft?.conflict?.text ?? null}
        onKeepLocalDraft={() => {
          void resolveDraftConflict(conversationId, 'local');
        }}
        onKeepRemoteDraft={() => {
          void resolveDraftConflict(conversationId, 'remote').then(async () => {
            const updated = await getDraft(conversationId);
            setDraftText(updated?.text ?? '');
          });
        }}
        members={isGroup || isChannel ? members : []}
        topics={isGroup ? topics : []}
        topicId={topicId}
        onTopicChange={setTopicId}
        canPublishSilently={isChannel && canPublish(viewer)}
        silent={silent}
        onSilentChange={setSilent}
        canCreatePoll={isGroup || (isChannel && canPublish(viewer))}
      />

      {deleting ? (
        <DeleteDialog
          // Section 06: a direct-chat author can delete for everyone without a
          // time limit, and a group or channel administrator can delete any
          // message. A recipient may only delete for themselves.
          canDeleteForEveryone={deleting.outgoing || canDeleteOthers}
          onConfirm={(scope) => {
            void confirmDelete(scope);
          }}
          onCancel={() => {
            setDeleting(null);
          }}
        />
      ) : null}

      {forwarding ? (
        <ForwardDialog
          sourceProtected={conversation?.protectedContent ?? false}
          onForward={(destinationId) => {
            const source = forwarding;
            setForwarding(null);
            void enqueueForwardMessage({
              sourceConversationId: conversationId,
              messageId: source.id,
              destinationConversationId: destinationId,
              originDeviceId: account.deviceId,
            }).then(queued);
          }}
          onClose={() => {
            setForwarding(null);
          }}
        />
      ) : null}

      {creatingPoll ? (
        <CreatePollDialog
          onCreate={(input) => {
            setCreatingPoll(false);
            void enqueueCreatePoll({
              conversationId,
              originDeviceId: account.deviceId,
              ...input,
            }).then(queued);
          }}
          onClose={() => {
            setCreatingPoll(false);
          }}
        />
      ) : null}
    </main>
  );
}

/** Placeholder route content shown beside the inbox when nothing is selected. */
export function NoConversationSelected() {
  const { t } = useStrings();
  const navigate = useNavigate();
  return (
    <main className={styles.screen} aria-label={t('nav.conversation')}>
      <p className={styles.notice}>{t('inbox.selectPrompt')}</p>
      <button
        type="button"
        className={panels.action}
        onClick={() => {
          void navigate('/');
        }}
      >
        {t('nav.inbox')}
      </button>
    </main>
  );
}
